import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { logger } from 'src/lib/logger';
import { useQueryClient } from '@tanstack/react-query';
import { setupAuthInterceptors, setSessionToken, scheduleProactiveRefresh, clearProactiveRefresh } from 'src/lib/api/session';
import { authStorage } from 'src/lib/auth/authStorage';
import { useAuthState, authKeys, User } from 'src/hooks/auth.ts';
import { deviceKeys } from 'src/hooks/device.ts';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
  isServerDown: boolean;
  redirectPath: string;
  setRedirectPath: (path: string) => void;
  clearRedirectPath: () => void;
  signOut: () => Promise<void>;
  setCredentials: (user: User) => void;
  refetchUser: () => Promise<unknown>;
  loginWithToken: (sessionToken: string, refreshToken?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isValidRedirectPath(path: string): boolean {
  return path !== '/sign-out' && path !== '/sign-in' && path.startsWith('/') && !path.startsWith('//');
}

function readInitialRedirectPath(): string {
  const storedPath = localStorage.getItem('auth_redirect_path');
  if (storedPath && isValidRedirectPath(storedPath)) {
    return storedPath;
  }
  return '/';
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [redirectPath, setRedirectPathState] = useState<string>(readInitialRedirectPath);

  const queryClient = useQueryClient();

  const authState = useAuthState();

  useEffect(() => {
    if (authState.user?.id) {
      logger.setContext({ userId: authState.user.id });
      logger.info('[AuthProvider] User context set for logging', { userId: authState.user.id });
      scheduleProactiveRefresh();
    } else {
      logger.setContext({ userId: null });
      clearProactiveRefresh();
    }
    return () => clearProactiveRefresh();
  }, [authState.user?.id]);

  useLayoutEffect(() => {
    setupAuthInterceptors(() => {
      logger.warn('[AuthProvider] Auth failure - clearing session');
      queryClient.removeQueries({ queryKey: authKeys.all });
      queryClient.removeQueries({ queryKey: deviceKeys.all });
      const publicPaths = ['/sign-in', '/sign-out', '/sign-up', '/verify-email', '/auth/callback'];
      const currentPath = window.location.pathname;
      const isPublicPath = publicPaths.some((path) => currentPath.startsWith(path));
      if (!isPublicPath) {
        const currentSearch = window.location.search;
        const currentUrl = `${currentPath}${currentSearch}`;
        const encodedRedirect = encodeURIComponent(currentUrl);
        window.location.href = `/sign-in?redirect=${encodedRedirect}`;
      }
    });
  }, [queryClient]);

  const setRedirectPath = useCallback((path: string): void => {
    if (path === '/sign-out' || path === '/sign-in' || !path.startsWith('/') || path.startsWith('//')) {
      return;
    }

    setRedirectPathState(path);
    localStorage.setItem('auth_redirect_path', path);
  }, []);

  const clearRedirectPath = useCallback((): void => {
    setRedirectPathState('/');
    localStorage.removeItem('auth_redirect_path');
  }, []);

  useEffect(() => {
    const storedPath = localStorage.getItem('auth_redirect_path');
    if (storedPath && !isValidRedirectPath(storedPath)) {
      localStorage.removeItem('auth_redirect_path');
    }
  }, []);

  const setCredentials = useCallback(
    (newUser: User): void => {
      queryClient.setQueryData(authKeys.me(), newUser);
    },
    [queryClient],
  );

  const loginWithToken = useCallback(
    async (token: string, refreshToken?: string): Promise<void> => {
      setSessionToken(token);
      if (refreshToken) {
        authStorage.saveRefreshToken(refreshToken);
      }
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    [queryClient],
  );

  const signOut = useCallback(async (): Promise<void> => {
    logger.info('[AuthProvider] Sign out initiated');
    try {
      clearProactiveRefresh();
      logger.debug('[AuthProvider] Calling sign out mutation');
      await authState.signOut();
      logger.info('[AuthProvider] Sign out completed successfully');

      logger.info('[Auth] Logout complete');
    } catch (error) {
      logger.error('[AuthProvider] Sign out error, forcing cleanup', { error });
    }
  }, [authState]);

  const contextValue: AuthContextType = {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    isError: authState.isError,
    isServerDown: authState.isServerDown,
    refetchUser: authState.refetchUser,
    redirectPath,
    setRedirectPath,
    clearRedirectPath,
    signOut,
    setCredentials,
    loginWithToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
